import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMessageTemplate, getMessageTemplates, templateToEmailContent, templateToMessageContent } from '@app/services/messageTemplates';
import { getApiErrorResponse } from '@app/services/api';
import { DEFAULT_EMAIL_DESIGN_CONTENT, DEFAULT_MESSAGE_DESIGN_CONTENT, MessageTemplateChannel } from '@app/utils/consts';
import { EmailDesignContent, MessageDesignContent, MessageTemplate } from '@app/models';

// The list endpoint caps `limit` at 100, which is enough for a dropdown
const PICKER_LIMIT = 100;

type DesignContent = EmailDesignContent | MessageDesignContent;

const toDesignContent = (channel: MessageTemplateChannel, template?: MessageTemplate): DesignContent => {
  if (channel === MessageTemplateChannel.EMAIL) {
    return template ? templateToEmailContent(template) : DEFAULT_EMAIL_DESIGN_CONTENT;
  }

  return template ? templateToMessageContent(template) : DEFAULT_MESSAGE_DESIGN_CONTENT;
};

const isNotFound = (error: unknown) => getApiErrorResponse(error).status === 404;

// Resolves what a channel sends, in the backend's order: custom content, linked template, business default, built-in default
export const useResolvedDesignTemplate = (channel: MessageTemplateChannel, templateId: string | null, customContent?: DesignContent | null) => {
  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['message-templates', 'list', { channel, limit: PICKER_LIMIT }],
    queryFn: () => getMessageTemplates({ channel, limit: PICKER_LIMIT }).then((res) => res.data.templates),
    // Off app-wide; here it picks up templates deleted or added in another tab while a form is open
    refetchOnWindowFocus: true,
  });

  const listedTemplate = templateId ? templates.find((template) => template.id === templateId) : undefined;

  // A linked template missing from the list is either past the first 100 or deleted; only a direct fetch tells which
  const linkedQuery = useQuery({
    // Same key and response as the template edit page, so they share the cache
    queryKey: ['message-templates', 'item', templateId],
    queryFn: () => getMessageTemplate(templateId as string).then((res) => res.data as MessageTemplate),
    enabled: Boolean(templateId) && !isLoading && !isError && !listedTemplate,
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
  });
  // A template of the other channel would be rejected on save just like a deleted one
  const fetchedTemplate = linkedQuery.data?.channel === channel ? linkedQuery.data : undefined;
  const isMissing = Boolean(templateId) && !listedTemplate && (isNotFound(linkedQuery.error) || (linkedQuery.isSuccess && !fetchedTemplate));

  const defaultTemplate = templates.find((template) => template.isDefault);
  const linkedTemplate = listedTemplate ?? fetchedTemplate;
  const selectedTemplate = templateId ? linkedTemplate : defaultTemplate;
  const isCustom = Boolean(customContent);
  // Until the template is known, its content would be a guess (the built-in default)
  const isResolving = isLoading || (Boolean(templateId) && !linkedTemplate && linkedQuery.isFetching);
  const isUnavailable = !isResolving && (isError || (Boolean(templateId) && !linkedTemplate));

  // The default is listed once as `null`, so changing the default later reaches everything that uses it.
  // A link to the current default's id (it was made default after linking) stays listed on its own
  const options = useMemo(
    () => [
      { value: null as string | null, label: defaultTemplate ? `Business default (${defaultTemplate.name})` : 'Business default' },
      ...templates.filter((template) => !template.isDefault || template.id === templateId).map((template) => ({ value: template.id as string | null, label: template.name })),
      ...(fetchedTemplate && !listedTemplate ? [{ value: fetchedTemplate.id as string | null, label: fetchedTemplate.name }] : []),
    ],
    [templates, defaultTemplate, templateId, fetchedTemplate, listedTemplate]
  );

  const selectedLabel = options.find((option) => option.value === templateId)?.label ?? (isResolving ? 'Loading...' : 'Template unavailable');
  const basedOnName = templateId ? (selectedTemplate?.name ?? 'a deleted template') : 'Business default';
  const content = customContent ?? toDesignContent(channel, selectedTemplate ?? defaultTemplate);
  const title = isCustom ? `Custom design (based on ${basedOnName})` : selectedLabel;

  return { options, selectedLabel, basedOnName, content, title, isCustom, isResolving, isUnavailable, isListError: isError, isMissing, refetch };
};
